import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { BanIcon, MoreVerticalIcon, PencilIcon, SendIcon, Trash2Icon } from 'lucide-react';
import { type MediaGuideline } from '@/entities/media-guideline';
import { useTranslation } from 'react-i18next';

interface GuidelineRowActionsProps {
  guideline: MediaGuideline;
  hasOpenDraft: boolean;
  onEdit: (guideline: MediaGuideline) => void;
  onPublish: (guideline: MediaGuideline) => void;
  onRetire: (guideline: MediaGuideline) => void;
  onDelete: (guideline: MediaGuideline) => void;
}

export function GuidelineRowActions({
  guideline,
  hasOpenDraft,
  onEdit,
  onPublish,
  onRetire,
  onDelete,
}: GuidelineRowActionsProps) {
  const { t } = useTranslation('knowledge');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="mini"
          aria-label={`Actions for ${guideline.name}`}
          className="size-9 p-0"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVerticalIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onSelect={() => onEdit(guideline)}>
          <PencilIcon className="mr-2 size-4" />
          {hasOpenDraft
            ? t('mediaGuidelines.guidelineRowActions.editDraft')
            : t('mediaGuidelines.guidelineRowActions.newVersion')}
        </DropdownMenuItem>
        {/*
          Publishing needs an open draft to publish. Kept enabled and answered on
          use rather than disabled, per the design system's restraint rule.
        */}
        <DropdownMenuItem onSelect={() => onPublish(guideline)}>
          <SendIcon className="mr-2 size-4" />
          {t('mediaGuidelines.guidelineRowActions.publish')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {guideline.status === 'active' && (
          <DropdownMenuItem onSelect={() => onRetire(guideline)}>
            <BanIcon className="mr-2 size-4" />
            {t('mediaGuidelines.guidelineRowActions.stopEnforcing')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem variant="destructive" onSelect={() => onDelete(guideline)}>
          <Trash2Icon className="mr-2 size-4" />
          {t('mediaGuidelines.guidelineRowActions.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
